/**
 * Authentication Context
 * Manages authentication state and provides auth-related functionality
 */

import type { ReactNode } from "react";
import { createContext, useContext, useState, useEffect, useRef } from "react";
import {
  supabase,
  handleAuthError,
  signOutCompletely,
  sessionExpiry,
} from "../services/supabase";
import { classifyPath, isLoginPath } from "../services/sessionExpiry";

import type {
  Profile,
  LoginCredentials,
  UserPermissions,
  AuthResponse,
} from "../types/auth.types";
import { SessionWarningModal } from "../components/SessionWarningModal";
import { getDefaultRouteForRole } from "../utils/roleRouting";
import {
  setSessionStartTime,
  clearSessionStartTime,
  isSessionExpired,
  getSessionStartTime,
  shouldShowSessionWarning,
  getSessionTimeRemaining,
  resetSessionTimeout,
} from "../utils/sessionTimeout";

// User profile caching
const USER_CACHE_KEY = "usls_user_profile";
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Write a diagnostic crumb to localStorage just before any auth-driven
 * redirect-to-login. Persists across the redirect so we can ask the
 * user `localStorage.getItem('usls_last_logout_reason')` and see which
 * code path triggered the logout. Called by forceLogout() and by the
 * SIGNED_OUT + TOKEN_REFRESHED handlers that redirect directly
 * (bypassing forceLogout).
 *
 * Without this instrumentation, every logout source looks identical on
 * the login page (just `?expired=true`) — the 2026-05-19 incident with
 * mpletenova@uslawshield.com surfaced that gap when all three
 * diagnostic markers came back null even after the prior hotfix.
 */
function recordLogoutReason(
  source: string,
  extra?: Record<string, unknown>,
): void {
  try {
    localStorage.setItem(
      "usls_last_logout_reason",
      JSON.stringify({
        at: new Date().toISOString(),
        source,
        ...(extra ?? {}),
      }),
    );
  } catch {
    // Non-critical — localStorage may be disabled (incognito, full, etc.)
  }
}

interface CachedUser {
  profile: Profile;
  timestamp: number;
}

interface AuthContextType {
  user: Profile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  permissions: UserPermissions | null;
  login: (credentials: LoginCredentials) => Promise<AuthResponse>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

/**
 * A failed or empty profile FETCH is not evidence that an account was deleted.
 * Until 2026-09-11 every such failure - a rotated refresh token on a woken tab, a
 * network blip, an aborted request - was reported to the user as
 * "Your account has been deleted" AND force-logged them out. PROD evidence: zero
 * 4xx/5xx on /rest/v1/profiles all day, the reporting users' profiles present and
 * is_active, and a spike of /auth/v1/token 400s (failed refresh grants) in the same
 * window. Nobody had been deleted.
 *
 * Rule: only say "deleted"/"deactivated" when we have actually READ a profile that
 * says so. Absence or error means the session is gone, nothing more.
 */
const SESSION_ENDED_MESSAGE = "Your session has expired. Please sign in again.";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

// Cache utilities
function cacheUserProfile(profile: Profile): void {
  try {
    const cached: CachedUser = {
      profile,
      timestamp: Date.now(),
    };
    localStorage.setItem(USER_CACHE_KEY, JSON.stringify(cached));
  } catch {
    // Silently fail - cache is non-critical
  }
}

function getCachedUserProfile(): Profile | null {
  try {
    const cached = localStorage.getItem(USER_CACHE_KEY);
    if (!cached) {
      return null;
    }

    const { profile, timestamp }: CachedUser = JSON.parse(cached);

    // Check if cache is still valid
    if (Date.now() - timestamp > CACHE_DURATION) {
      localStorage.removeItem(USER_CACHE_KEY);
      return null;
    }

    return profile;
  } catch {
    return null;
  }
}

function clearCachedUserProfile(): void {
  try {
    localStorage.removeItem(USER_CACHE_KEY);
  } catch {
    // Silently fail - cleanup is non-critical
  }
}

// Redirect an unauthenticated/no-role visitor to /login, first preserving the
// originally-requested URL in sessionStorage 'returnTo' so LoginForm can return
// them there after sign-in. Used by every protected-route redirect (RequireAuth,
// the cold-load init no-session path, and RequireRole's no-role case) so the
// destination is never lost regardless of which guard fires first. Open-redirect
// safe: relative same-origin path only (mirrors LoginForm's guard).
function redirectToLogin(): void {
  try {
    const returnTo = window.location.pathname + window.location.search;
    if (
      returnTo &&
      returnTo !== "/login" &&
      returnTo.startsWith("/") &&
      !returnTo.startsWith("//")
    ) {
      sessionStorage.setItem("returnTo", returnTo);
    }
  } catch {
    // Ignore sessionStorage errors (private mode, etc.)
  }
  window.location.href = "/login";
}

// Get user profile from database
async function getCurrentUserProfile(): Promise<Profile | null> {
  console.log("[Auth] getCurrentUserProfile: Starting");

  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();

  if (userError) {
    console.error("[Auth] getCurrentUserProfile: getUser error:", userError);
    handleAuthError(userError);
    throw userError;
  }

  if (!user) {
    console.log("[Auth] getCurrentUserProfile: No user found");
    return null;
  }

  const { data: profile, error } = await supabase
    .from("profiles")
    .select("*")
    .eq("id", user.id)
    .single();

  if (error) {
    console.error("[Auth] getCurrentUserProfile: Profile query error:", error);
    handleAuthError(error);
    throw error;
  }

  if (!profile) {
    throw new Error("Profile not found");
  }

  return profile as Profile;
}

// Calculate permissions based on user role
function calculatePermissions(profile: Profile): UserPermissions {
  const role = profile.role?.toLowerCase();

  return {
    can_create_organizations: ["super_admin", "superadmin", "admin"].includes(
      role,
    ),
    can_manage_users: ["super_admin", "superadmin", "admin", "it"].includes(
      role,
    ),
    can_view_all_contacts: [
      "super_admin",
      "superadmin",
      "admin",
      "manager",
      "regional_manager",
      "territory_manager",
      "staff",
    ].includes(role),
    can_export_data: [
      "super_admin",
      "superadmin",
      "admin",
      "manager",
      "regional_manager",
      "territory_manager",
      "finance",
    ].includes(role),
    can_delete_records: ["super_admin", "superadmin", "admin"].includes(role),
    can_manage_billing: ["super_admin", "superadmin", "finance"].includes(role),
  };
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<Profile | null>(null);
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [sessionTimeRemaining, setSessionTimeRemaining] = useState(0);
  const userLoadedRef = useRef(false);
  const logoutInProgressRef = useRef(false);
  const loginInProgressRef = useRef(false);

  // Login function
  const login = async (
    credentials: LoginCredentials,
  ): Promise<AuthResponse> => {
    console.log("[Auth] Login called");
    // Reset logout flag in case we're logging in after a logout
    logoutInProgressRef.current = false;

    try {
      // Clear THIS tab's app-level caches before login — but NOT the shared
      // Supabase session. localStorage is shared across tabs, so wiping the sb-*
      // session token or calling signOut here logs OTHER tabs out (the reported
      // multi-tab logout bug). signInWithPassword below REPLACES any existing
      // session atomically, so no pre-signOut / localStorage wipe is needed.
      console.log("[Auth] Clearing local caches before login (multi-tab safe)");
      clearSessionStartTime();
      clearCachedUserProfile();
      // Guard the transient SIGNED_OUT that can fire as the session is replaced.
      loginInProgressRef.current = true;
    } catch {
      // Continue anyway
    }

    // Sign in with email and password
    console.log("[Auth] Calling signInWithPassword...");
    const { data, error } = await supabase.auth.signInWithPassword({
      email: credentials.email,
      password: credentials.password,
    });
    // Session is now established — SIGNED_OUT events from the preceding signOut
    // are no longer a risk; clear the guard so normal SIGNED_OUT handling resumes.
    loginInProgressRef.current = false;

    if (error) {
      console.error("[Auth] signInWithPassword error:", error.message);
      throw new Error(error.message);
    }

    if (!data.user) {
      console.error("[Auth] No user returned from signInWithPassword");
      throw new Error("No user returned from login");
    }
    console.log("[Auth] signInWithPassword succeeded, user:", data.user.id);

    // Set session start time
    setSessionStartTime();

    // Get user profile directly using the user ID we already have
    // (avoids redundant getUser() call that can hang)
    console.log("[Auth] Getting user profile for:", data.user.id);
    let profile: Profile | null = null;
    try {
      const { data: profileData, error: profileError } = await Promise.race([
        supabase.from("profiles").select("*").eq("id", data.user.id).single(),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error("Profile fetch timeout")), 8000),
        ),
      ]);

      if (profileError) {
        throw profileError;
      }
      profile = profileData as Profile;
      console.log("[Auth] Got profile:", profile?.id, profile?.role);
    } catch (error) {
      console.error("[Auth] Profile fetch failed or timed out:", error);

      // Retry once with a fresh query
      if (error instanceof Error && error.message === "Profile fetch timeout") {
        console.log("[Auth] Timeout detected, retrying profile fetch...");
        try {
          const { data: retryData, error: retryError } = await Promise.race([
            supabase
              .from("profiles")
              .select("*")
              .eq("id", data.user.id)
              .single(),
            new Promise<never>((_, reject) =>
              setTimeout(() => reject(new Error("Retry timeout")), 5000),
            ),
          ]);

          if (retryError) {
            throw retryError;
          }
          profile = retryData as Profile;
          console.log("[Auth] Retry succeeded, got profile:", profile?.id);
        } catch (retryError) {
          console.error("[Auth] Retry also failed:", retryError);
          await supabase.auth.signOut();
          throw new Error(
            "Unable to load your profile. Please clear your browser cache and try again.",
          );
        }
      } else {
        // A failed fetch is not a deletion - see SESSION_ENDED_MESSAGE.
        await supabase.auth.signOut();
        throw new Error(SESSION_ENDED_MESSAGE);
      }
    }

    if (!profile) {
      // Absent != deleted. getCurrentUserProfile() returns null for "no session"
      // AND for a transient failure, so absence proves nothing about the account.
      await supabase.auth.signOut();
      throw new Error(SESSION_ENDED_MESSAGE);
    }

    // Check if account was soft-deleted (email starts with "deleted_")
    if (profile.email?.startsWith("deleted_")) {
      await supabase.auth.signOut();
      throw new Error(
        "Your account has been deleted. Please contact support for assistance.",
      );
    }

    // Check if account is active
    if (!profile.is_active) {
      await supabase.auth.signOut();
      throw new Error(
        "Your account has been deactivated. Please contact support for assistance.",
      );
    }

    // Update profile with last login
    await supabase
      .from("profiles")
      .update({ last_login: new Date().toISOString() })
      .eq("id", profile.id);

    // Cache the profile
    cacheUserProfile(profile);

    // Update state
    setUser(profile);
    setPermissions(calculatePermissions(profile));
    userLoadedRef.current = true;

    return {
      user: profile,
      session: {
        access_token: data.session!.access_token,
        refresh_token: data.session!.refresh_token,
        expires_in: data.session!.expires_in,
        expires_at: new Date(data.session!.expires_at! * 1000).toISOString(),
      },
    };
  };

  // Logout function
  /**
   * Explicit, user-initiated sign-out.
   *
   * ⭐ ORDER IS LOAD-BEARING — do not "clear state first" again.
   *
   * This used to call setUser(null) BEFORE awaiting signOut(). That re-renders RequireAuth,
   * which calls redirectToLogin() synchronously, and the navigation ABORTS the in-flight
   * POST /auth/v1/logout. supabase-js only reaches _removeSession() AFTER that request
   * resolves, and it returns early on a network error (it swallows only 401/403/404), so the
   * token survived in localStorage. The user then landed on /login still holding a valid
   * session, and the multi-tab guard in initializeAuth() did exactly what it is written to do:
   * sent them back into the app as the SAME user. That is the "I log out and it logs me
   * straight back in" report — a race, not a browser bug. Safari lost it every time; Chrome
   * usually won it, which is why it looked Safari-specific.
   *
   * So: finish the sign-out, THEN let the UI react.
   */
  const logout = async () => {
    // Prevent duplicate logout attempts
    if (logoutInProgressRef.current) {
      return;
    }

    logoutInProgressRef.current = true;

    // Sign out FIRST, while the app is still mounted and nothing is navigating.
    // signOutCompletely() finishes before it returns AND removes the token even if the
    // request failed or timed out — both guarantees matter; see services/supabase.ts.
    await signOutCompletely();

    // Only now let the UI react. The route guard's redirect can no longer abort anything.
    setUser(null);
    setPermissions(null);
    userLoadedRef.current = false;
    clearSessionStartTime();
    clearCachedUserProfile();
  };

  // Force logout with reason
  const forceLogout = async (logoutReason?: string, source?: string) => {
    // Prevent duplicate logout attempts (important for StrictMode and race conditions).
    // sessionExpiry.isSigningOut(): the session-expiry handler (or another signOutCompletely
    // caller) is already signing out and owns the redirect — a second sign-out here would race
    // it to a different /login URL. (2026-10-03.)
    if (logoutInProgressRef.current || sessionExpiry.isSigningOut()) {
      return;
    }

    logoutInProgressRef.current = true;

    // Write a diagnostic crumb BEFORE the redirect so we can ask the
    // user what triggered the logout. Source is passed by the caller
    // (health-check-threshold, session-timeout, signed-out-event,
    // token-refresh-failure, init-failure, etc.); reason is the
    // user-facing string we put in the URL.
    recordLogoutReason(source ?? "forceLogout-unsourced", {
      reason: logoutReason ?? null,
    });

    // Same guarantees as logout(); see services/supabase.ts.
    await signOutCompletely();

    setUser(null);
    setPermissions(null);
    userLoadedRef.current = false;
    clearSessionStartTime();
    clearCachedUserProfile();

    // Redirect to login with reason (unless on public pages that handle their own flow)
    // One route classifier for the whole app (services/sessionExpiry.ts). The five
    // hand-copied lists this replaced had drifted: none had /event-register, so a
    // public registrant holding a stale token was bounced to /login. (2026-10-03)
    const isNonProtectedPage =
      classifyPath(window.location.pathname) !== "protected";

    if (!isNonProtectedPage) {
      // Store current URL so user can return after re-login
      const returnTo = window.location.pathname + window.location.search;
      if (returnTo && returnTo !== "/login") {
        sessionStorage.setItem("returnTo", returnTo);
      }
      const reason = logoutReason || "Session expired";
      window.location.href = `/login?expired=true&reason=${encodeURIComponent(reason)}`;
    }
  };

  // Refresh user profile
  const refreshUser = async () => {
    try {
      const profile = await getCurrentUserProfile();

      if (profile) {
        // Check if user was deleted
        if (profile.email?.startsWith("deleted_")) {
          await forceLogout(
            "Your account has been deleted. Please contact support for assistance.",
          );
          return;
        }

        // Check if user is still active
        if (!profile.is_active) {
          await forceLogout(
            "Your account has been deactivated. Please contact support for assistance.",
          );
          return;
        }

        setUser(profile);
        setPermissions(calculatePermissions(profile));
        cacheUserProfile(profile);
      }
    } catch (error) {
      // A refresh that throws means the session is gone, not the account.
      await forceLogout(SESSION_ENDED_MESSAGE);
      throw error;
    }
  };

  // Extend session handler
  const handleExtendSession = () => {
    resetSessionTimeout();
    setShowSessionWarning(false);
  };

  // Initialize auth state on mount
  useEffect(() => {
    let mounted = true;

    const initAuth = async () => {
      try {
        // Skip initialization on public pages
        // One route classifier for the whole app (services/sessionExpiry.ts). The five
        // hand-copied lists this replaced had drifted: none had /event-register, so a
        // public registrant holding a stale token was bounced to /login. (2026-10-03)
        const isNonProtectedPage =
          classifyPath(window.location.pathname) !== "protected";

        if (isNonProtectedPage) {
          console.log("[Init] On auth/public page - skipping initialization");
          // Clear any stale session data on login page to prevent conflicts
          if (isLoginPath(window.location.pathname)) {
            logoutInProgressRef.current = false;
            // MULTI-TAB SAFE (fix for cross-tab logout): do NOT clear the
            // browser-SHARED Supabase session (sb-* localStorage) or signOut
            // here. localStorage is shared across tabs, so wiping it makes OTHER
            // tabs' clients fire SIGNED_OUT and bounce to /login — the reported
            // bug (open /login in tab B → tab A gets logged out).
            //
            // Instead: if a VALID session already exists, the user is already
            // signed in (opened a /login bookmark, or duplicated a tab) — send
            // them into the app rather than destroying the session. Signing in
            // from the form replaces the session atomically, so no pre-clear is
            // needed for the logged-out case.
            try {
              const {
                data: { session },
              } = await supabase.auth.getSession();
              const stillValid =
                !!session?.user &&
                (!session.expires_at || session.expires_at * 1000 > Date.now());
              if (stillValid) {
                console.log(
                  "[Init] Valid session on /login — redirecting into the app (no signOut, tabs preserved)",
                );
                window.location.replace("/");
                return;
              }
              console.log(
                "[Init] No valid session on /login — showing the login form",
              );
            } catch (e) {
              console.warn(
                "[Init] getSession on /login failed; showing the login form",
                e,
              );
            }
            // Logged-out: clear only THIS tab's app caches (not the shared token).
            clearSessionStartTime();
            clearCachedUserProfile();
          }

          setIsLoading(false);
          return;
        }

        // Try to get cached profile first
        const cachedProfile = getCachedUserProfile();

        if (cachedProfile) {
          // Check if cached user was deleted
          if (cachedProfile.email?.startsWith("deleted_")) {
            await forceLogout(
              "Your account has been deleted. Please contact support for assistance.",
            );
            return;
          }

          // Check if cached user is active
          if (!cachedProfile.is_active) {
            await forceLogout(
              "Your account has been deactivated. Please contact support for assistance.",
            );
            return;
          }

          userLoadedRef.current = true;
          setUser(cachedProfile);
          setPermissions(calculatePermissions(cachedProfile));
          setIsLoading(false);

          // Refresh profile in background
          setTimeout(async () => {
            try {
              if (mounted) {
                const freshProfile = await getCurrentUserProfile();
                if (freshProfile && mounted) {
                  // Check if user was deleted
                  if (freshProfile.email?.startsWith("deleted_")) {
                    await forceLogout(
                      "Your account has been deleted. Please contact support for assistance.",
                    );
                    return;
                  }

                  // Check if user is still active
                  if (!freshProfile.is_active) {
                    await forceLogout(
                      "Your account has been deactivated. Please contact support for assistance.",
                    );
                    return;
                  }

                  setUser(freshProfile);
                  setPermissions(calculatePermissions(freshProfile));
                  cacheUserProfile(freshProfile);
                } else if (mounted) {
                  // BACKGROUND refresh: the user is already working with a valid
                  // cached profile. A failed background fetch must never evict them -
                  // this ran 100ms after every load, so it was maximally exposed to
                  // exactly the transient failures it used to misreport as deletion.
                  console.warn(
                    "[Auth] Background profile refresh returned nothing; keeping cached profile.",
                  );
                }
              }
            } catch (bgError) {
              if (mounted) {
                // Same reasoning as above: a background refresh failure is not a
                // reason to sign anyone out. The next foreground check will catch a
                // genuinely dead session.
                console.warn(
                  "[Auth] Background profile refresh failed; keeping cached profile.",
                  bgError,
                );
              }
            }
          }, 100);

          return;
        }

        // Check for existing session with timeout to handle hanging requests
        let session = null;
        try {
          const sessionPromise = supabase.auth.getSession();
          const timeoutPromise = new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error("Session check timeout")), 5000),
          );
          const result = await Promise.race([sessionPromise, timeoutPromise]);
          session = result.data.session;
        } catch (e) {
          console.warn("[Init] Session check failed or timed out:", e);
        }

        if (!session) {
          console.log("[Init] No valid session, redirecting to login");
          setIsLoading(false);
          // Preserve the requested URL — this cold-load init path can fire on a
          // fresh deep-link visit before RequireAuth mounts.
          redirectToLogin();
          return;
        }

        // Verify session hasn't expired
        const sessionStart = getSessionStartTime();
        if (!sessionStart) {
          // Session exists but no start time - set it now
          setSessionStartTime();
        } else if (isSessionExpired()) {
          await forceLogout("Your session has expired");
          return;
        }

        // Load user profile
        let profile;
        try {
          profile = await getCurrentUserProfile();
        } catch {
          // Throwing here means we could not reach the profile, not that it is gone.
          if (mounted) {
            await forceLogout(SESSION_ENDED_MESSAGE);
          }
          return;
        }

        if (profile && mounted) {
          // Check if user was deleted
          if (profile.email?.startsWith("deleted_")) {
            await forceLogout(
              "Your account has been deleted. Please contact support for assistance.",
            );
            return;
          }

          // Check if user is active
          if (!profile.is_active) {
            await forceLogout(
              "Your account has been deactivated. Please contact support for assistance.",
            );
            return;
          }

          userLoadedRef.current = true;
          setUser(profile);
          setPermissions(calculatePermissions(profile));
          cacheUserProfile(profile);
        } else if (mounted) {
          await forceLogout(SESSION_ENDED_MESSAGE);
        }
      } catch {
        if (mounted) {
          await forceLogout("Authentication error");
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    initAuth();

    // Subscribe to auth changes
    // NOTE: callback must be synchronous — Supabase uses BroadcastChannel internally,
    // and an async callback keeps the message channel open until the promise resolves.
    // If the channel closes first (tab switch, token refresh, navigation) you get
    // "A listener indicated an asynchronous response … but the message channel closed".
    // Fix: return synchronously, fire async work in a detached void IIFE.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // ⭐ SYNCHRONOUS on purpose (before the async IIFE). auth-js emits SIGNED_OUT while it
      // still holds its storage lock, and the page's queued data requests resume the moment it
      // releases. The expiry flag must be set by then or they go out as anon — the 2026-10-03
      // "permission denied for function has_org_access" storm. See services/sessionExpiry.ts.
      if (event === "SIGNED_IN") {
        sessionExpiry.reset();
      } else if (
        event === "SIGNED_OUT" &&
        !loginInProgressRef.current &&
        !logoutInProgressRef.current
      ) {
        // Protected page: one sign-out + redirect with the "session expired" message.
        // Auth/public page: no-op (they run logged out or own their session flow).
        void sessionExpiry.handle("supabase-signed-out-event");
      }
      void (async () => {
        console.log("[Auth] Auth state change:", event);
        if (event === "SIGNED_IN" && session?.user) {
          // Skip if we're on a public page
          // One route classifier for the whole app (services/sessionExpiry.ts). The five
          // hand-copied lists this replaced had drifted: none had /event-register, so a
          // public registrant holding a stale token was bounced to /login. (2026-10-03)
          const isNonProtectedPage =
            classifyPath(window.location.pathname) !== "protected";

          if (isNonProtectedPage) {
            console.log(
              "[Auth] SIGNED_IN on auth/public page - skipping handler",
            );
            return;
          }

          // SIGNED_IN means user just logged in - always set/reset session start time
          setSessionStartTime();

          if (!userLoadedRef.current) {
            try {
              const profile = await getCurrentUserProfile();
              if (profile && mounted) {
                userLoadedRef.current = true;
                setUser(profile);
                setPermissions(calculatePermissions(profile));
                cacheUserProfile(profile);
                setIsLoading(false);
              }
            } catch {
              // Silent fail - login page will handle errors
            }
          }
        } else if (event === "SIGNED_OUT") {
          // Ignore SIGNED_OUT events fired by the deliberate signOut({scope:"local"})
          // inside login() — that call is just clearing the old session before signing
          // in fresh. Without this guard the event can race with the post-login page
          // load and clear state that the new session already set.
          if (loginInProgressRef.current) {
            console.log("[Auth] SIGNED_OUT during login flow — ignoring");
            return;
          }
          // ...and likewise during a DELIBERATE sign-out. auth-js notifies subscribers
          // synchronously from _removeSession(), so with the corrected ordering this branch
          // now runs on every voluntary logout — where it would stamp the forensic crumb
          // "supabase-signed-out-event" and redirect to ?expired=true&reason=Session+expired.
          // That crumb exists (2026-05-19 incident) precisely to tell logout SOURCES apart,
          // so letting a user-initiated Sign Out masquerade as a Supabase expiry destroys the
          // one signal it was built to provide — and shows a false "Session expired" banner.
          // logout()/forceLogout() own the redirect; this handler is for sessions that end
          // WITHOUT us asking. Other tabs are unaffected: their ref is false, so cross-tab
          // sign-out still works. (Pre-PROD audit, 2026-09-21.)
          if (logoutInProgressRef.current) {
            console.log(
              "[Auth] SIGNED_OUT from our own logout — the logout path owns the redirect",
            );
            return;
          }
          // The session-expiry handler (started synchronously above) or another
          // signOutCompletely() caller owns this one: it signs out, THEN navigates. Clearing
          // React state here first would re-render RequireAuth into a plain /login redirect
          // that aborts the sign-out — the 2026-09-21 ordering bug.
          if (sessionExpiry.isSigningOut()) {
            console.log(
              "[Auth] SIGNED_OUT — session-expiry handler owns the redirect",
            );
            return;
          }
          // Auth/public page (classifyPath !== "protected"): nothing to redirect; just drop
          // any signed-in state so the page renders as the logged-out visitor it is.
          console.log(
            "[Auth] SIGNED_OUT on a",
            classifyPath(window.location.pathname),
            "page — clearing state, no redirect",
          );
          userLoadedRef.current = false;
          setUser(null);
          setPermissions(null);
          clearSessionStartTime();
          clearCachedUserProfile();
        } else if (event === "TOKEN_REFRESHED" && session?.user) {
          // Skip if we're on a public page
          // One route classifier for the whole app (services/sessionExpiry.ts). The five
          // hand-copied lists this replaced had drifted: none had /event-register, so a
          // public registrant holding a stale token was bounced to /login. (2026-10-03)
          const isNonProtectedPage =
            classifyPath(window.location.pathname) !== "protected";
          if (isNonProtectedPage) {
            return;
          }

          // Check if we have a session start time - if not, we're logging out
          const sessionStart = getSessionStartTime();
          if (!sessionStart) {
            return;
          }

          // Check if session has expired
          if (isSessionExpired()) {
            await forceLogout(
              "Your session has expired",
              "token-refreshed-isSessionExpired",
            );
            return;
          }

          // Update profile
          try {
            const profile = await getCurrentUserProfile();
            if (profile && mounted) {
              setUser(profile);
              cacheUserProfile(profile);
            }
          } catch (refreshErr) {
            // The fetch above failed BECAUSE the session is dead, and the session-expiry
            // handler has already started its single sign-out + redirect. Don't start a second.
            if (sessionExpiry.isSigningOut()) {
              return;
            }
            // ⭐ SAME ORDERING RULE AS logout() — see the comment there. Clearing state here
            // would re-render RequireAuth, whose synchronous redirectToLogin() aborts the
            // signOut() below, leaving the token in localStorage and bouncing the user
            // straight back into the app. State is cleared AFTER the sign-out, further down.
            // (Found by the pre-PROD audit, 2026-09-21: this is the second instance of the
            // bug the same commit fixed in logout().)

            // 2026-05-19: instrument this path. TOKEN_REFRESHED catch
            // fires when the post-refresh profile fetch fails — could
            // be transient network OR a real "user was deleted while
            // logged in" case. Previously redirected with no marker.
            recordLogoutReason("token-refreshed-profile-fetch-failed", {
              error:
                refreshErr instanceof Error
                  ? refreshErr.message?.slice(0, 200)
                  : String(refreshErr).slice(0, 200),
            });

            await signOutCompletely();

            userLoadedRef.current = false;
            setUser(null);
            setPermissions(null);
            clearSessionStartTime();

            window.location.href =
              "/login?expired=true&reason=Unable+to+refresh+profile";
          }
        }
      })(); // end void async IIFE
    });

    // Cleanup
    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  // Monitor session timeout
  useEffect(() => {
    if (!user) {
      return;
    }

    const checkInterval = setInterval(() => {
      const timeRemaining = getSessionTimeRemaining();
      setSessionTimeRemaining(timeRemaining);

      if (timeRemaining === 0) {
        // Session expired - force logout
        clearInterval(checkInterval);
        forceLogout("Your session has expired", "session-timeout-1s-interval");
      } else if (shouldShowSessionWarning()) {
        // Show warning when < 5 minutes remaining
        setShowSessionWarning(true);
      }
    }, 1000); // Check every second

    // visibilitychange fires when the tab becomes visible again.
    // Browsers throttle/suspend setInterval in background tabs, so the
    // 1-second timeout check above may never fire while the tab is hidden.
    // This handler catches expired sessions immediately when the user returns.
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const timeRemaining = getSessionTimeRemaining();
        setSessionTimeRemaining(timeRemaining);

        if (timeRemaining === 0) {
          clearInterval(checkInterval);
          forceLogout(
            "Your session has expired",
            "session-timeout-visibilitychange",
          );
        } else if (shouldShowSessionWarning()) {
          setShowSessionWarning(true);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(checkInterval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [user]);

  // Periodic session health check — failure-tolerant edition (2026-05-19
  // hotfix). The original implementation called supabase.auth.getUser()
  // (a network call) every 60 seconds and force-logged-out on ANY error.
  // That cascaded transient network blips, 5xx responses, and even
  // backgrounded-tab throttling into mass false-positive logouts (16
  // /login?expired=true redirects across 9 prod IPs in one morning).
  //
  // New design:
  //   - Skip the check entirely if the tab is hidden (browser throttling
  //     of network requests in background tabs causes apparent failures
  //     that aren't real).
  //   - Use getSession() which reads from localStorage — no network call,
  //     no transient flakiness to chase. supabase-js's own auto-refresh
  //     loop handles actual token-refresh failures and emits SIGNED_OUT
  //     when the refresh-token chain is genuinely dead.
  //   - Track CONSECUTIVE failures in a ref. A single missing-session
  //     observation does not force logout. Only force logout after the
  //     threshold (3 consecutive checks = roughly 3 minutes of
  //     confirmed missing session) is crossed.
  //   - Write a diagnostic marker to localStorage on every failure so
  //     we can forensically reconstruct what triggered any future
  //     logout.
  useEffect(() => {
    if (!user) {
      return;
    }

    const FAILURE_THRESHOLD = 3;
    let consecutiveFailures = 0;

    const healthCheckInterval = setInterval(async () => {
      if (document.hidden) {
        // Tab is backgrounded — skip the check. The browser may throttle
        // any network/timer work and the apparent state isn't reliable.
        return;
      }

      try {
        // Local-only: reads from localStorage; no network. supabase-js's
        // auto-refresh loop is what actually keeps the access token
        // fresh and emits SIGNED_OUT if the refresh chain breaks.
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();

        if (error) {
          // getSession only fails for storage-access errors. Treat as
          // transient — log + count.
          consecutiveFailures += 1;
          console.warn(
            "[Auth] health check getSession error",
            `(${consecutiveFailures}/${FAILURE_THRESHOLD}):`,
            error,
          );
          try {
            localStorage.setItem(
              "usls_last_health_failure",
              JSON.stringify({
                at: new Date().toISOString(),
                reason: "getSession-error",
                count: consecutiveFailures,
                error: (error as Error)?.message?.slice(0, 200) ?? null,
              }),
            );
          } catch {
            /* non-critical */
          }
        } else if (!session) {
          // Session is genuinely gone from local storage. Could be
          // another tab signed out, or auto-refresh failed and the
          // SIGNED_OUT event fired but the listener missed it. Count
          // but don't immediately redirect — wait until we're confident.
          consecutiveFailures += 1;
          console.warn(
            "[Auth] health check: no session in storage",
            `(${consecutiveFailures}/${FAILURE_THRESHOLD})`,
          );
          try {
            localStorage.setItem(
              "usls_last_health_failure",
              JSON.stringify({
                at: new Date().toISOString(),
                reason: "no-session",
                count: consecutiveFailures,
              }),
            );
          } catch {
            /* non-critical */
          }
        } else {
          // Healthy — reset.
          if (consecutiveFailures > 0) {
            console.log(
              "[Auth] health check recovered after",
              consecutiveFailures,
              "failures",
            );
          }
          consecutiveFailures = 0;
        }
      } catch (err) {
        consecutiveFailures += 1;
        console.warn(
          "[Auth] health check threw",
          `(${consecutiveFailures}/${FAILURE_THRESHOLD}):`,
          err,
        );
        try {
          localStorage.setItem(
            "usls_last_health_failure",
            JSON.stringify({
              at: new Date().toISOString(),
              reason: "thrown",
              count: consecutiveFailures,
              error: (err as Error)?.message?.slice(0, 200) ?? null,
            }),
          );
        } catch {
          /* non-critical */
        }
      }

      if (consecutiveFailures >= FAILURE_THRESHOLD) {
        console.error(
          "[Auth] health check threshold reached, forcing logout after",
          consecutiveFailures,
          "consecutive failures",
        );
        // forceLogout() writes the diagnostic crumb via recordLogoutReason.
        // Pass the source so we can tell health-check-threshold from
        // other forceLogout call sites.
        forceLogout("Session check failed", "health-check-threshold");
      }
    }, 60000);

    return () => clearInterval(healthCheckInterval);
  }, [user]);

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    permissions,
    login,
    logout,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      <SessionWarningModal
        show={showSessionWarning}
        timeRemaining={sessionTimeRemaining}
        onExtend={handleExtendSession}
      />
      {children}
    </AuthContext.Provider>
  );
}

// RequireAuth component - protects routes that need authentication
interface RequireAuthProps {
  children: ReactNode;
}

export function RequireAuth({ children }: RequireAuthProps) {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Preserve the originally-requested URL so the user is returned here after
    // login (LoginForm reads sessionStorage 'returnTo'). See redirectToLogin.
    redirectToLogin();
    return null;
  }

  // Force-change gate: a user whose password was reset to a temporary one by an admin
  // must set a new password before using the app. While the flag is set,
  // /force-password-change is the ONLY allowed authenticated route (logout aside).
  if (
    user?.password_reset_required &&
    window.location.pathname !== "/force-password-change"
  ) {
    window.location.href = "/force-password-change";
    return null;
  }

  return <>{children}</>;
}

// RequireRole component - protects routes that need specific roles
interface RequireRoleProps {
  children: ReactNode;
  allowedRoles: string[];
}

export function RequireRole({ children, allowedRoles }: RequireRoleProps) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user || !user.role) {
    // No-role / corrupted-session case (rare): preserve the destination too.
    redirectToLogin();
    return null;
  }

  // Normalize role for comparison. Use /_/g so multi-underscore role
  // strings (e.g. a future "support_admin_v2") normalize correctly —
  // the prior single-pass .replace only handled the first underscore.
  const userRole = user.role.toLowerCase().replace(/_/g, "");
  const normalizedAllowedRoles = allowedRoles.map((r) =>
    r.toLowerCase().replace(/_/g, ""),
  );

  if (!normalizedAllowedRoles.includes(userRole)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8 text-center">
          <div className="mb-4">
            <svg
              className="mx-auto h-12 w-12 text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Access Denied
          </h2>
          <p className="text-gray-600 mb-6">
            You don't have permission to access this page.
          </p>
          <button
            onClick={() => {
              // Same centralized role→default-route mapping used by the root
              // redirect and LoginForm — no bespoke branching here.
              window.location.href = getDefaultRouteForRole(user.role);
            }}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
